// One recording for a ticket's reply (60.11.553; on its own since 60.11.579 so a screen recording outlives the ticket's
// page): its streams, the browser's MediaRecorder, its pauses and its limits, and the file it leaves.
import {
  pickRecordingType,
  recordingFileName,
  recordingFileType,
  SCREEN_MAX_BYTES,
} from './ticket-media'

export type TicketRecording = {file: File; url: string; seconds: number; kind: 'voice' | 'screen'}

export const stopStreams = (streams: MediaStream[]) =>
  streams.forEach((stream) => stream.getTracks().forEach((track) => track.stop()))

/** When a recording began, how long it has been paused, and since when it is paused now (0: it is not), in ms. */
export type RecordingClock = {started: number; pausedFor: number; pausedAt: number}

/** How long a recording has run by its clock, its pauses left out, in seconds. */
export const elapsedOnClock = (clock: RecordingClock, now = Date.now()) => {
  const pausedNow = clock.pausedAt ? now - clock.pausedAt : 0
  return (now - clock.started - clock.pausedFor - pausedNow) / 1000
}

export class RecordingSession {
  private readonly recorder: MediaRecorder
  private readonly chunks: Blob[] = []
  private bytes = 0
  private readonly started = Date.now()
  private pausedFor = 0
  private pausedAt = 0
  private cancelled = false

  /**
   * Starts at once. `onDone` is told once, when it stops: the recording, or null when it was cancelled or nothing was
   * recorded. A browser that cannot record throws, and the streams are the caller's to let go.
   */
  constructor(
    readonly kind: 'voice' | 'screen',
    private readonly streams: MediaStream[],
    types: string[],
    options: MediaRecorderOptions,
    private readonly maxSeconds: number,
    onDone: (recording: TicketRecording | null) => void
  ) {
    const mimeType = pickRecordingType(
      types,
      window.MediaRecorder.isTypeSupported?.bind(window.MediaRecorder)
    )
    const combined = new MediaStream(streams.flatMap((stream) => stream.getTracks()))
    const recorder = new MediaRecorder(combined, mimeType ? {...options, mimeType} : options)
    this.recorder = recorder
    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size) {
        this.chunks.push(event.data)
        this.bytes += event.data.size
      }
      // Its limits are kept here as well as by the clock on the screen: a hidden tab's clock may run once a minute.
      if (this.overLimit()) this.stop()
    }
    // Stopped by the user, by its limits, by the browser («توقف اشتراک‌گذاری»), or by a locked phone: what was
    // recorded joins the reply all the same, to be heard or removed before sending.
    recorder.onstop = () => {
      stopStreams(this.streams)
      const seconds = this.elapsed()
      if (this.cancelled || !this.chunks.length) {
        onDone(null)
        return
      }
      const {type, extension} = recordingFileType(recorder.mimeType || mimeType, kind)
      const file = new File(this.chunks, recordingFileName(kind, extension), {
        type,
        lastModified: Date.now(),
      })
      onDone({file, url: URL.createObjectURL(file), seconds, kind})
    }
    streams.forEach((stream) =>
      stream.getVideoTracks().forEach((track) => track.addEventListener('ended', () => this.stop()))
    )
    recorder.start(1000)
  }

  get paused() {
    return this.recorder.state === 'paused'
  }

  /** Its clock, for another window of the panel to show its time (60.11.580). */
  clock(): RecordingClock {
    return {started: this.started, pausedFor: this.pausedFor, pausedAt: this.pausedAt}
  }

  /** How long it has run, its pauses left out, in seconds. */
  elapsed() {
    return elapsedOnClock(this.clock())
  }

  overLimit() {
    return this.elapsed() >= this.maxSeconds || this.bytes >= SCREEN_MAX_BYTES
  }

  pause() {
    if (this.recorder.state !== 'recording') return false
    this.recorder.pause()
    this.pausedAt = Date.now()
    return true
  }

  resume() {
    if (this.recorder.state !== 'paused') return false
    this.recorder.resume()
    this.pausedFor += Date.now() - this.pausedAt
    this.pausedAt = 0
    return true
  }

  /** Kept, or with `cancel` thrown away. */
  stop(cancel = false) {
    this.cancelled = this.cancelled || cancel
    if (this.recorder.state !== 'inactive') this.recorder.stop()
    else stopStreams(this.streams)
  }
}
